import React, { useEffect, useMemo, useRef } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { usePrintActions } from "../../store/reportsStore";
import { mapCoursePeriodSectionsSortedByQuarter } from "../../../../js/Mappers";
import { getCoursePeriodIdForPeriod } from "../../../../js/PeriodHelpers";
import { capitalizeFirstLetters } from "../../../../js/Helpers";
import MagnetboardSection from "./MagnetboardSection";
import StudentMagnetboardSection, {
  getStudentMainSectionsForCoursePeriod,
} from "./StudentMagnetboardSection";
import { getDiagnosticTimestamp, logMagnetboardDiagnostic, startMagnetboardDiagnosticTimer } from "../utils/diagnostics";

const PRINT_ROW_LABEL_WIDTH = 120;

const MagnetboardPrintSchedule = React.memo(
  ({
    filteredDisplaySchedule,
    printOptions = {},
    scheduleData: {
      scheduleStructureData,
      sections,
      setSections,
      subsections,
      departments,
      schoolInfo,
      scheduleInfo,
      currentScheduleVersionID,
      scheduleType,
      currentView,
      currentViewType,
      currentDay,
      periodsToShow,
      teachers,
      courses,
      classrooms,
      labels,
      labs_only_filtered,
      non_labs_only_filtered,
      singletons_filtered,
      doubletons_filtered,
      no_classroom_filtered,
      has_classroom_filtered,
      studentSectionsByStudentCoursePeriod,
      studentConflictSectionLookup,
      sectionConflictSummaryBySectionId,
      sectionsById,
      subsectionParentById,
      teachersById,
      coursesById,
      classroomsById,
      labelsById,
    },
  }) => {
    const printRef = useRef(null);
    const renderCountRef = useRef(0);
    const renderStartedAt = getDiagnosticTimestamp();
    const renderTimerEnd = startMagnetboardDiagnosticTimer("print-schedule-render", {
      current_view: currentView,
      rows: filteredDisplaySchedule.length,
      periods: periodsToShow.length,
      sections: sections.length,
      subsections: subsections.length,
    });
    renderCountRef.current += 1;
    const { setPrintRef } = usePrintActions();
    const showClassroomInPrint = printOptions.showClassroom !== false;
    const showStudentCountInPrint = printOptions.showStudentCount !== false;
    const showConflictsWarningsInPrint = printOptions.showConflictsWarnings !== false;
    const printOnePerPage = printOptions.printOnePerPage === true;
    const printCanvasWidth = printOptions.orientation === "portrait" ? "980px" : "1400px";

    useEffect(() => {
      setPrintRef(printRef);
      return () => setPrintRef(null);
    }, [setPrintRef]);

    useEffect(() => {
      logMagnetboardDiagnostic("print-schedule-commit", {
        render_count: renderCountRef.current,
        duration_ms: Math.round(getDiagnosticTimestamp() - renderStartedAt),
        current_view: currentView,
        rows: filteredDisplaySchedule.length,
        periods: periodsToShow.length,
        sections: sections.length,
      });
    });

    const rowStyle = useMemo(
      () => ({
        gridTemplateColumns: `${PRINT_ROW_LABEL_WIDTH}px repeat(${periodsToShow.length}, minmax(0, 1fr))`,
      }),
      [periodsToShow.length]
    );

    const departmentsById = useMemo(
      () =>
        departments.reduce((result, department) => {
          result[department.department_id] = department.department;
          return result;
        }, {}),
      [departments]
    );

    const subsectionParents = useMemo(
      () => subsectionParentById || {},
      [subsectionParentById]
    );

    const periodType = useMemo(() => {
      if (scheduleType === "daily" || currentViewType === "day") return "schedule_periods";
      if (scheduleType === "ab" || scheduleType === "ab_skinny") return "day_course_periods";
      return "course_periods";
    }, [currentViewType, scheduleType]);

    const titleParts = useMemo(() => {
      const parts = [`${capitalizeFirstLetters(currentView)} View`];
      if (currentViewType === "day") parts.push(`Day ${currentDay}`);
      if (currentViewType === "period") parts.push("Period View");
      return parts;
    }, [currentDay, currentView, currentViewType]);

    const getRowSubtitle = (entity) => {
      if (currentView === "student") return entity.display_info?.grade || "";

      const entityDepartments = entity.display_info?.departments || [];
      return entityDepartments
        .map((departmentId) => departmentsById[departmentId])
        .filter(Boolean)
        .join(", ");
    };

    const getNormalizedPeriodId = (period_info, fallback_index = null) => {
      if (!period_info) return fallback_index;

      return (
        period_info.period_id ||
        period_info.schedule_period_id ||
        period_info.course_period_id ||
        fallback_index
      );
    };

    const getTeacherSections = (entityId) =>
      sections.filter(
        (section) =>
          section.teacher_id === entityId ||
          (Array.isArray(section.secondary_teachers) &&
            section.secondary_teachers.includes(entityId))
      );

    const rowsToPrint = useMemo(() => {
      if (currentView !== "teacher") return filteredDisplaySchedule;

      return filteredDisplaySchedule.filter((entity) =>
        sections.some(
          (section) =>
            (section.teacher_id === entity.data_id ||
              (Array.isArray(section.secondary_teachers) &&
                section.secondary_teachers.includes(entity.data_id))) &&
            Array.isArray(section.course_periods) &&
            section.course_periods.length > 0
        )
      );
    }, [currentView, filteredDisplaySchedule, sections]);

    const getCellSections = (entity, coursePeriodId) => {
      if (!coursePeriodId) return [];

      let dataSections = [];
      if (currentView === "teacher") {
        dataSections = getTeacherSections(entity.data_id);
      } else if (currentView === "course") {
        dataSections = sections.filter(
          (section) =>
            section.course_id === entity.data_id &&
            Array.isArray(section.course_periods) &&
            section.course_periods.includes(coursePeriodId)
        );
      } else if (currentView === "classroom") {
        dataSections = sections.filter(
          (section) =>
            section.classroom_id === entity.data_id &&
            Array.isArray(section.course_periods) &&
            section.course_periods.includes(coursePeriodId)
        );
      } else if (currentView === "student") {
        return getStudentMainSectionsForCoursePeriod({
          studentId: entity.data_id,
          coursePeriodId,
          studentSectionsByStudentCoursePeriod,
          sectionsById,
          subsectionParentById: subsectionParents,
        });
      }

      const uniqueSections = dataSections.reduce(
        (result, section) => {
          const mainSectionId = subsectionParents[section.section_id] || section.section_id;
          const alreadyAdded = result.loggedSectionIds.has(mainSectionId);

          if (
            !alreadyAdded &&
            Array.isArray(section.course_periods) &&
            section.course_periods.includes(coursePeriodId)
          ) {
            result.loggedSectionIds.add(mainSectionId);
            result.sections.push(section);
          }

          return result;
        },
        {
          loggedSectionIds: new Set(),
          sections: [],
        }
      );

      return mapCoursePeriodSectionsSortedByQuarter(uniqueSections.sections);
    };

    const renderHeaderRow = (keyPrefix = "print-header") => (
      <div className="mb-row mb-middle-header mb-print-header-row" style={rowStyle}>
        <div className="mb-main-inner-box capitalize">{currentView}</div>
        {periodsToShow.map((periodInfo, periodIndex) => {
          const periodId = getNormalizedPeriodId(periodInfo, periodIndex);

          return (
            <div className="mb-inner-box" key={`${keyPrefix}-${periodId}`}>
              <div>{periodInfo.period_name}</div>
            </div>
          );
        })}
      </div>
    );

    const renderedSchedule = (
      <DndProvider backend={HTML5Backend}>
        <div
          ref={printRef}
          className="mb-print-screen-hidden"
          style={{
            position: "fixed",
            left: "-10000px",
            top: 0,
            width: printCanvasWidth,
            padding: "0 12px 12px 12px",
            backgroundColor: "#fff",
          }}
        >
          <div className="mb-print-shell">
            <div className="mb-print-header">
              <div className="mb-print-title">Magnetboard Schedule</div>
              <div className="mb-print-subtitle">{titleParts.join(" • ")}</div>
            </div>

            {!printOnePerPage && renderHeaderRow()}

            <div className="mb-print-grid">
              {rowsToPrint.map((entity, entityIndex) => (
                <div
                  key={entity.data_id || `print-row-${entityIndex}`}
                  className={`mb-schedule-row-parent mb-print-schedule-row-parent ${printOnePerPage ? "mb-print-one-row-per-page" : ""}`}
                >
                  {printOnePerPage && renderHeaderRow(`print-header-${entity.data_id || entityIndex}`)}
                  <div className="mb-row mb-schedule-row mb-print-schedule-row" style={rowStyle}>
                    <div className="mb-schedule-main-inner-box">
                      <div className="mb-schedule-row-name-container">
                        <div className="mb-schedule-row-name">
                          {currentView === "student"
                            ? `${entity.display_info?.last_name || ""}, ${entity.display_info?.first_name || ""}`
                            : entity.display_name}
                        </div>
                        <div className="mb-schedule-row-department dark-gray-text">
                          {getRowSubtitle(entity)}
                        </div>
                      </div>
                    </div>

                    {periodsToShow.map((periodInfo, periodIndex) => {
                      const periodId = getNormalizedPeriodId(periodInfo, periodIndex);
                      const coursePeriodId = getCoursePeriodIdForPeriod(
                        scheduleStructureData,
                        periodId,
                        {
                          period_type: periodType,
                          schedule_type: scheduleType,
                          current_day: currentDay,
                        }
                      );

                      const isLunchPeriod =
                        currentViewType === "day" &&
                        `${periodId}` === `${entity.display_info?.lunch_schedule_period_id ?? ""}`;
                      const cellSections = getCellSections(entity, coursePeriodId);

                      return (
                        <div
                          className="mb-inner-box mb-schedule-inner-box mb-print-schedule-inner-box"
                          key={`${entity.data_id || entityIndex}-${periodId}`}
                        >
                          {isLunchPeriod && currentView === "student" && (
                            <div className="mb-student-section">
                              <div className="mb-section-top-bar pastel-yellow">
                                <div>Lunch</div>
                              </div>
                              <div className="mb-section-content">
                                <div className="mb-section-name ellipsis">Lunch</div>
                              </div>
                            </div>
                          )}

                          {cellSections.map((sectionInfo, sectionIndex) =>
                            currentView === "student" ? (
                              <StudentMagnetboardSection
                                key={`${entity.data_id || entityIndex}-${periodId}-${sectionInfo.section_id || sectionIndex}`}
                                sectionInfo={sectionInfo}
                                scheduleInfo={scheduleInfo}
                                scheduleType={scheduleType}
                                currentViewType={currentViewType}
                                currentDay={currentDay}
                                studentID={entity.data_id}
                                sectionsById={sectionsById}
                                teachersById={teachersById}
                                coursesById={coursesById}
                                classroomsById={classroomsById}
                                labelsById={labelsById}
                                studentConflictSectionLookup={studentConflictSectionLookup}
                                isPrintMode={true}
                                printDisplayOptions={{
                                  showClassroom: showClassroomInPrint,
                                  showStudentCount: showStudentCountInPrint,
                                  showConflictsWarnings: showConflictsWarningsInPrint,
                                }}
                              />
                            ) : (
                              <MagnetboardSection
                                key={`${entity.data_id || entityIndex}-${periodId}-${sectionInfo.section_id || sectionInfo.subsection_id || sectionIndex}`}
                                schoolInfo={schoolInfo}
                                scheduleInfo={scheduleInfo}
                                scheduleStructureData={scheduleStructureData}
                                scheduleType={scheduleType}
                                currentViewType={currentViewType}
                                currentView={currentView}
                                currentDay={currentDay}
                                currentScheduleVersionID={currentScheduleVersionID}
                                teachers={teachers}
                                courses={courses}
                                classrooms={classrooms}
                                sections={sections}
                                setSections={setSections}
                                subsections={subsections}
                                labels={labels}
                                setConflictsLoading={() => {}}
                                labsOnlyFiltered={labs_only_filtered}
                                nonLabsOnlyFiltered={non_labs_only_filtered}
                                singletonsFiltered={singletons_filtered}
                                doubletonsFiltered={doubletons_filtered}
                                noClassroomFiltered={no_classroom_filtered}
                                hasClassroomFiltered={has_classroom_filtered}
                                toggleEditSection={() => {}}
                                dataID={entity.data_id}
                                coursePeriodID={coursePeriodId}
                                sectionID={sectionInfo.section_id}
                                studentID={currentView === "student" ? entity.data_id : null}
                                sectionType="section"
                                handleSelectQuickViewDataID={null}
                                selectedQuickViewSectionID={null}
                                toggleLockSection={() => {}}
                                isDraggable={false}
                                isEditable={false}
                                isLockable={false}
                                isQuickViewEnabled={false}
                                sectionConflictSummaryBySectionId={sectionConflictSummaryBySectionId}
                                isPrintMode={false}
                                printDisplayOptions={{
                                  showClassroom: showClassroomInPrint,
                                  showStudentCount: showStudentCountInPrint,
                                  showConflictsWarnings: showConflictsWarningsInPrint,
                                }}
                                magnetboardOptions={{ allow_inline_quarter_editing: false }}
                              />
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DndProvider>
    );

    renderTimerEnd({rows_to_print: rowsToPrint.length});
    return renderedSchedule;
  }
);

export default MagnetboardPrintSchedule;
